<template>
  <div>
    <p class="mb-4">
      {{ $t(`${TRANSLATION_KEY_PREFIX}.${envelopeType === ENVELOPE_TYPES.SINGLE ? 'subtitleSingleEnvelope' : 'subtitleMultipleEnvelopes'}`) }}
    </p>

    <div class="generic-signers-list">
      <SigningChecklistSignersAssignationListItem
        v-for="(genericSignature, index) in genericSignaturesList"
        :key="genericSignature.id"
        :index="index + 1"
        class="generic-signers-list-item"
        :envelopeType="envelopeType"
        :generic-signature="genericSignature"
        :signers="signers"
        @assign-signer="onSignerAssign"
      />
    </div>

    <div v-if="additionalSigners.length > 0" class="additional-signers-list">
      <p class="mb-4">
        {{ $t(`${TRANSLATION_KEY_PREFIX}.${envelopeType === ENVELOPE_TYPES.SINGLE ? 'additionalSignersSubtitleSingleEnvelope' : 'additionalSignersSubtitleMultipleEnvelopes'}`) }}
      </p>

      <div>
        <p
          v-for="signer in additionalSigners"
          class="additional-signers-list-item"
          :key="signer.id"
        >
          {{ signer.name }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import { ENVELOPE_TYPES, SIGNATURE_TYPES } from '@/project/signing-checklist/constants'
import { SignersColors } from '@/project/signing-checklist/prepare-signature/signers-colors'
import { GenericSignature } from '@/project/views/SigningChecklistAdd.vue'

import SigningChecklistSignersAssignationListItem, { GenericSignatureListItem } from './SigningChecklistSignersAssignationListItem.vue'

const TRANSLATION_KEY_PREFIX = 'project.signing-checklist.add.step2.signers-assignation.SigningChecklistSignersAssignationList'

export default defineComponent({
  name: 'SigningChecklistSignersAssignationList',
  components: { SigningChecklistSignersAssignationListItem },
  props: {
    envelopeType: {
      type: String as PropType<ENVELOPE_TYPES>,
      required: true,
    },
    signers: {
      type: Array as PropType<Array<EnvelopeSigner>>,
      default: () => [],
    },
    templateSignatures: {
      type: Array as PropType<Array<GenericSignature>>,
      default: () => [],
    },
  },
  data () {
    return {
      ENVELOPE_TYPES,
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    genericSignaturesList (): Array<GenericSignatureListItem> {
      return this.templateSignatures
        .filter(templateSignature => templateSignature.signerType === SIGNATURE_TYPES.SINGLE)
        .map((templateSignature, index) => {
          const signerColor = SignersColors[index % (Object.entries(SignersColors).length / 2)]
          return {
            ...templateSignature,
            signerColor: signerColor,
          }
        })
    },
    additionalSigners (): Array<EnvelopeSigner> {
      return this.signers.filter(
        signer => this.genericSignaturesList.every(genericSignature => genericSignature.assignedSigner?.id !== signer.id),
      )
    },
  },
  methods: {
    onSignerAssign (signatureId: GenericSignature['id'], signer: EnvelopeSigner) {
      this.$emit('assign-signer', signatureId, signer)
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/scss/border-tokens";

.generic-signers-list,
.additional-signers-list {
  border: get-border();
  @include border-radius('border-radius-3');
}

.generic-signers-list {
  &:empty {
    display: none;
  }

  &-item {
    padding: 16px;

    &:not(:last-child) {
      border-bottom: get-border();
    }
  }
}

.additional-signers-list {
  padding: 16px;
  margin-top: 24px;

  &-item {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 8px;
    @include border-radius('border-radius-3');
    color: rgb(var(--v-theme-text-grey));
    background-color: rgb(var(--v-theme-surface-grey-subtle));

    &:not(:last-child) {
      margin-right: 8px;
    }
  }
}
</style>
